import { useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { agentTrpc } from 'src/lib/agent-trpc';

import { todayIso } from 'src/sections/makeup/use-class-lesson-dates';

// ----------------------------------------------------------------------

export const BOARD_QUERY_KEY = 'lessonPlansBoard';

/**
 * The Lesson Plans board (epic #474): the course's units and lessons with readiness,
 * pacing dates and exit ticket results — rows for `classId` when given. The agent
 * caches 60 s, so the first load on each visit and every `refresh()` ask it to
 * recompute (the teacher may have just edited a lesson in the editor).
 */
/** Whose schedule to show (epic #484): a section, or the class-less baseline. */
export type BoardSchedule = { classId: string } | 'baseline';

/**
 * Query options for one board read — the same key as `useLessonPlansBoard`, so pages that
 * read many boards at once (the Calendar) share the cache with it. Takes the agent's 60 s
 * cache (no `fresh`).
 */
export function boardQueryOptions(courseId: string, classId: string | null, schedule: BoardSchedule) {
  return {
    queryKey: [BOARD_QUERY_KEY, courseId, classId, schedule === 'baseline' ? 'baseline' : schedule.classId],
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: (count: number, err: Error) => !/not in this course/i.test(err?.message ?? '') && count < 3,
    queryFn: () =>
      agentTrpc.lessonPlan.board({
        courseId,
        today: todayIso(),
        ...(classId ? { classId } : {}),
        schedule,
      }),
  };
}

export function useLessonPlansBoard(
  courseId: string | null,
  classId: string | null,
  schedule: BoardSchedule,
  /** false: take the agent's 60 s cache on first load (the dashboard reads every class at once). */
  freshOnMount = true
) {
  const freshRef = useRef(freshOnMount);
  const query = useQuery({
    queryKey: [BOARD_QUERY_KEY, courseId, classId, schedule === 'baseline' ? 'baseline' : schedule.classId],
    enabled: !!courseId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    // A rejected input (e.g. a schedule section from another course) won't pass on retry.
    retry: (count, err) => !/not in this course/i.test(err?.message ?? '') && count < 3,
    queryFn: () => {
      const fresh = freshRef.current;
      freshRef.current = false;
      return agentTrpc.lessonPlan.board({
        courseId: courseId!,
        today: todayIso(),
        ...(classId ? { classId } : {}),
        schedule,
        ...(fresh ? { fresh: true } : {}),
      });
    },
  });
  const { refetch } = query;
  const refresh = useCallback(() => {
    freshRef.current = true;
    return refetch();
  }, [refetch]);
  return { ...query, refresh };
}
